/**
 * PdfGenerationModal Component (Story 5.12, Task 4.3)
 *
 * Progress modal shown during PDF generation with status updates.
 * AC #1: Shows progress states ("Rendering charts...", "Assembling PDF...", "Uploading...")
 */

import { useEffect, useRef, useState } from "react";
import { Spinner, Check, Warning } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import type { PdfStatus } from "../../hooks/useGeneratePdf";

interface PdfGenerationModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Current PDF generation status */
  status: PdfStatus;
  /** Error message when status is "failed" */
  error: string | null;
  /** Callback when user clicks retry */
  onRetry: () => void;
  /** Callback when user closes the modal */
  onClose: () => void;
}

/** Progress steps shown during generation */
const PROGRESS_STEPS = [
  "Rendering charts...",
  "Assembling PDF...",
  "Uploading...",
];

export function PdfGenerationModal({
  isOpen,
  status,
  error,
  onRetry,
  onClose,
}: PdfGenerationModalProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Simulate progress steps during generation
  useEffect(() => {
    if (status !== "generating") {
      setStepIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setStepIndex((prev) =>
        prev < PROGRESS_STEPS.length - 1 ? prev + 1 : prev
      );
    }, 2000);

    return () => clearInterval(interval);
  }, [status]);

  // Handle Escape key to close modal (only when not generating)
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && status !== "generating") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, status, onClose]);

  // Auto-focus the dialog when opened
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen, status]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="PDF generation progress"
      data-testid="pdf-generation-modal"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="mx-4 w-full max-w-sm rounded-lg bg-white p-6 shadow-xl outline-none"
      >
        {/* Generating state */}
        {status === "generating" && (
          <div className="text-center" data-testid="pdf-generating-state">
            <Spinner
              size={32}
              className="mx-auto mb-4 animate-spin text-primary"
            />
            <h3 className="mb-2 text-lg font-semibold">Generating PDF</h3>
            <p className="text-sm text-muted-foreground">
              {PROGRESS_STEPS[stepIndex]}
            </p>
            {/* Progress indicator */}
            <div className="mt-4 flex justify-center gap-1.5">
              {PROGRESS_STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-8 rounded-full transition-colors ${
                    i <= stepIndex ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Success state */}
        {status === "completed" && (
          <div className="text-center" data-testid="pdf-completed-state">
            <Check
              size={32}
              weight="bold"
              className="mx-auto mb-4 text-green-600"
            />
            <h3 className="mb-2 text-lg font-semibold">PDF Ready</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Your report has been generated successfully.
            </p>
            <Button onClick={onClose} data-testid="pdf-done-btn">
              Done
            </Button>
          </div>
        )}

        {/* Error state */}
        {status === "failed" && (
          <div className="text-center" data-testid="pdf-failed-state">
            <Warning
              size={32}
              weight="bold"
              className="mx-auto mb-4 text-red-600"
            />
            <h3 className="mb-2 text-lg font-semibold">Generation Failed</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {error ?? "An unexpected error occurred. Please try again."}
            </p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={onClose} data-testid="pdf-cancel-btn">
                Cancel
              </Button>
              <Button onClick={onRetry} data-testid="pdf-retry-btn">
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
